import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { Header } from 'listings/interfaces/header.interface';
import { Listing } from 'listings/interfaces/listing.interface';
import { ListingsService } from 'listings/services/listings.service';
import { BehaviorSubject, catchError, combineLatest, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { FeatureGroup } from 'shared/interfaces/feature-group.interface';
import { PermissionsService, StorageService } from 'shared/services';
import { IceVisualListingStatusApiResponse, legacyVisualsListingStatusMap } from '../interfaces/ice-visual-listing.interface';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';

const VISUALS_API_URL = environment.BASE_URL + 'visualManagementApi';
@Injectable({
    providedIn: 'root',
})
export class VisualsListingsService {
    refreshListingStatus$ = new BehaviorSubject<void>(undefined);

    public warmupEnded = signal<boolean>(false);

    loadListingStatus = signal<boolean>(false);
    loadListingStatus$ = toObservable(this.loadListingStatus);

    listingId = signal<string | null>(null);
    forVisualsTab = signal<boolean | null>(false);

    profileFeatureGroupId = computed(() => {
        const featureGroups = this.featureGroups();
        if (!featureGroups) {
            return null;
        }
        return featureGroups.find((group) => group.fieldName === 'listingProfile')?.id;
    });

    listingHasVisualsEnabled = computed(() => {
        const listing = this.listing();
        const listingId = this.listingId();
        if (!listing || !listingId) {
            return null;
        }
        return listing.hasVisualsAccess || false;
    });

    isHeaderCollapsed = signal(false);

    public listingId$ = toObservable(this.listingId);
    public forVisualsTab$ = toObservable(this.forVisualsTab);

    featureGroups = toSignal<FeatureGroup[]>(
        this.listingId$.pipe(switchMap((id) => (id ? this.listingsService.getFeatureGroups(id) : of([])))),
    );

    constructor(
        private http: HttpClient,
        private listingsService: ListingsService,
        private permissionsService: PermissionsService,
        private storageService: StorageService,
    ) {}

    // Initializing the preconditionFailed signal to true, to prevent the loading of visuals data and components
    // until the listing status data is loaded and the we know that the preconditions are met.
    preconditionFailed = signal<boolean>(true);

    destroy(): void {
        this.preconditionFailed.set(false);
        this.loadListingStatus.set(false);
        this.warmupEnded.set(false);
    }

    private listingStatusData$ = combineLatest([this.listingId$, this.loadListingStatus$, this.refreshListingStatus$]).pipe(
        switchMap(([id, loadListingStatus]) => {
            if (!loadListingStatus || !id) {
                return of(null);
            }
            return this.getListingStatusData(id);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    listingStatusData = toSignal(this.listingStatusData$, { initialValue: null });

    listing = toSignal<Listing | null>(
        combineLatest([this.listingId$, this.forVisualsTab$, this.refreshListingStatus$]).pipe(
            switchMap(([id, forVisualsTab]) => {
                if (!id) return of(null);

                return combineLatest([
                    this.listingsService.getListingById(id),
                    this.listingStatusData$,
                    this.permissionsService.userHasAccess('listings.saveOptions.submitForPublishing'),
                ]).pipe(
                    map(([listing, statusData, canSubmit]) => {
                        if (forVisualsTab && listing && statusData) {
                            listing.listingStatus = legacyVisualsListingStatusMap[statusData.status];
                            listing.maxVersion = statusData.listingInstanceId!;

                            if (!listing.isReadOnly) {
                                listing.isReadOnly = listing.listingStatus === ListingStatusEnum.APPROVAL && !canSubmit;
                            }
                        }
                        // Since signals use the object's reference to check and determine if a value has changed,
                        // we need to return a new object each time to ensure that the dependents (computes, effects, etc) get updated.
                        return { ...listing };
                    }),
                );
            }),
        ),
        { initialValue: null },
    );

    headerData = computed<Header>(() => {
        const header = {
            title: this.listing()?.name,
            shijiId: this.listing()?.shijiId,
            supplierId: this.listing()?.supplierId,
            imgSrc: this.listing()?.supplierLogo,
            listingId: this.listing()?.id,
            addressValidationStatus: this.listing()?.addressValidationStatus,
        } as Header;
        return header;
    });

    getListingStatusData(listingId: string) {
        return this.http
            .get<IceVisualListingStatusApiResponse>(`${VISUALS_API_URL}/listings/get-listing-status-data?listingId=${listingId}`)
            .pipe(
                tap(() => {
                    this.preconditionFailed.set(false);
                }),
                catchError((error) => {
                    if (error.status === 412) {
                        this.preconditionFailed.set(true);
                    }
                    return of(null);
                }),
            );
    }

    getVisualValidationErrors(listingId: string) {
        return this.http.get<unknown>(`${VISUALS_API_URL}/listings/can-make-live?listingId=${listingId}`);
    }

    createDraftListing(listingId: string) {
        const url = new URL(`${VISUALS_API_URL}/listings/create-draft?listingId=${listingId}`);

        return this.http.post(url.toString(), {});
    }

    dropDraftListing(listingId: string) {
        const url = new URL(`${VISUALS_API_URL}/listings/discard-draft?listingId=${listingId}`);

        return this.http.post(url.toString(), {});
    }

    submitListing(listingId: string) {
        const url = new URL(`${VISUALS_API_URL}/listings/publish-listing?listingId=${listingId}`);

        return this.http.post(url.toString(), {}).pipe(
            finalize(() => {
                this.storageService.setInLocal('publishedListing', true);
            }),
        );
    }

    submitListingForReview(listingId: string) {
        const url = new URL(`${VISUALS_API_URL}/listings/submit-for-review?listingId=${listingId}`);

        return this.http.post(url.toString(), {});
    }

    returnListingToEditor(listingId: string, rejectReason: string) {
        const url = new URL(`${VISUALS_API_URL}/listings/return-to-editor?listingId=${listingId}`);

        return this.http.post(url.toString(), JSON.stringify(rejectReason), { headers: { 'Content-Type': 'application/json' } });
    }

    deleteListing(listingId: string) {
        const url = new URL(`${VISUALS_API_URL}/listings?listingId=${listingId}`);

        return this.http.delete(url.toString(), {});
    }

    warmupListing() {
        const listingId = this.listingId();
        const url = new URL(`${VISUALS_API_URL}/listings/warmup-listing?listingId=${listingId}`);

        return this.http.get<void>(url.toString());
    }

    getListingAssetsCategoryCoverage() {
        const listingId = this.listingId();

        const url = new URL(`${VISUALS_API_URL}/listings/category-coverage?listingId=${listingId}`);
        return this.http.get(url.toString(), { context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [400]) });
    }
}
